import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  getClasses,
  deleteClassOffering,
  formatClassDateRange,
  type ClassOffering,
} from '../../features/classes';
import { getImageUrlById } from '../../features/images';
import ClassOfferingModal from '../../components/dashboard/ClassOfferingModal';
import { DashboardPageHeader, DeleteConfirmModal } from '../../shared/ui';
import { getClassShareUrl, getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';

const ClassesManagementPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [list, setList] = useState<ClassOffering[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ClassOffering | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState<ClassOffering | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canView = !!profile && can(profile, 'classes', 'view');
  const canEdit = !!profile && can(profile, 'classes', 'edit');
  const canDelete = !!profile && can(profile, 'classes', 'delete');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClasses();
      setList(data);
    } catch (err) {
      logErrorInDev('Failed to load classes', err);
      setError(getUserFriendlyErrorMessage(err, 'Failed to load classes.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) void load();
  }, [canView]);

  const handleSaved = () => {
    showToast('Class saved successfully.');
    setShowModal(false);
    setEditing(null);
    void load();
  };

  const handleDeleteClick = (item: ClassOffering) => {
    setToDelete(item);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteClassOffering(toDelete.id);
      setList((prev) => prev.filter((x) => x.id !== toDelete.id));
      setShowDelete(false);
      setToDelete(null);
      showToast('Class deleted.');
    } catch (err) {
      logErrorInDev('Failed to delete class', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to delete class.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const copyClassLink = async (e: React.MouseEvent, item: ClassOffering) => {
    e.stopPropagation();
    const url = getClassShareUrl(item.id);
    try {
      await navigator.clipboard.writeText(url);
      showToast('Link copied to clipboard.');
    } catch {
      showToast('Failed to copy link.', 'error');
    }
  };

  if (!canView) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          <i className="bi bi-shield-exclamation me-2" />
          You do not have permission to view classes.
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <DashboardPageHeader
        title="Classes"
        description="Manage class offerings shown on the public Classes page when marked public."
        icon="bi bi-book"
        actions={
          canEdit ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { setEditing(null); setShowModal(true); }}
            >
              <i className="bi bi-plus-lg me-1" /> Add Class
            </button>
          ) : null
        }
      />

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
      ) : list.length === 0 ? (
        <div className="alert alert-info">No classes yet. Add one to show on the public Classes page (when marked Public).</div>
      ) : (
        <div className="row g-4">
          {list.map((item) => (
            <div key={item.id} className="col-12 col-sm-6 col-lg-4">
              <div className="class-offering-card">
                <div
                  className="class-offering-card__image-wrap"
                  role={canEdit ? 'button' : undefined}
                  tabIndex={canEdit ? 0 : undefined}
                  onClick={canEdit ? () => { setEditing(item); setShowModal(true); } : undefined}
                  onKeyDown={canEdit ? (e) => { if (e.key === 'Enter') { setEditing(item); setShowModal(true); } } : undefined}
                  style={{ cursor: canEdit ? 'pointer' : 'default' }}
                >
                  {(item.imageIds?.length ? item.imageIds[0] : null) ? (
                    <img src={getImageUrlById(item.imageIds[0])} alt="" />
                  ) : (
                    <div className="class-offering-card__image-placeholder">
                      <i className="bi bi-image" />
                    </div>
                  )}
                </div>
                <div className="class-offering-card__body">
                  <h3 className="class-offering-card__title">{item.title}</h3>
                  {item.subtitle && <p className="class-offering-card__subtitle mb-0">{item.subtitle}</p>}
                  {(item.dateRangeStart != null || item.dateRangeEnd != null) && (
                    <p className="class-offering-card__date">
                      <i className="bi bi-calendar3" />
                      {formatClassDateRange(item.dateRangeStart, item.dateRangeEnd)}
                    </p>
                  )}
                </div>
                <div className="class-offering-card__footer">
                  <span className={`class-offering-card__badge ${item.isPublic ? 'class-offering-card__badge--public' : ''}`}>
                    {item.isPublic ? 'Public' : 'Draft'}
                  </span>
                  <div className="class-offering-card__actions">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={(e) => copyClassLink(e, item)}
                      title="Copy link to share"
                    >
                      <i className="bi bi-link-45deg me-1" /> Copy link
                    </button>
                    {canEdit && (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={(e) => { e.stopPropagation(); setEditing(item); setShowModal(true); }}
                    >
                      <i className="bi bi-pencil me-1" /> Edit
                    </button>
                    )}
                    {canDelete && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={(e) => { e.stopPropagation(); handleDeleteClick(item); }}
                    >
                      <i className="bi bi-trash me-1" /> Delete
                    </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ClassOfferingModal
          show={showModal}
          onHide={() => { setShowModal(false); setEditing(null); }}
          onSaved={handleSaved}
          offering={editing}
        />
      )}
      {showDelete && toDelete && (
        <DeleteConfirmModal
          show={showDelete}
          onHide={() => { setShowDelete(false); setToDelete(null); }}
          onConfirm={confirmDelete}
          itemName={toDelete.title}
          itemType="class"
          isLoading={deleting}
        />
      )}
    </div>
  );
};

export default ClassesManagementPage;
